import Guid from "_shared/guids/guid";
import BookmarkToggle from "marketplace/bookmarks/_views/bookmark-toggle";
import {
  useCreateCompanyBookmarkMutation,
  useDeleteCompanyBookmarkMutation,
} from "marketplace/bookmarks/_api/bookmark-api";
import useCompanyBookmarkStatus from "marketplace/bookmarks/_hooks/use-company-bookmark-status";
import { useSnackbar } from "_shared/_providers/snackbars/snackbars";

export interface CompanyBookmarkButtonProps {
  className?: string;
  companyId?: Guid;
  /** The result's name, which the toggle's accessible name repeats. */
  name: string;
}

export default function CompanyBookmarkButton(props: Readonly<CompanyBookmarkButtonProps>) {
  const { className, companyId, name } = props;

  const { enqueueSnackbar } = useSnackbar();

  // One company's bookmark, not the whole list: the hook puts the ids that every card on a results
  // page asks for in the same tick into one bulk request. Undefined while it loads, null for none.
  const { bookmark: existingBookmark, hasFailed, retry } = useCompanyBookmarkStatus(companyId);
  const [createCompanyBookmark, { isLoading: isCreating }] = useCreateCompanyBookmarkMutation();
  const [deleteCompanyBookmark, { isLoading: isDeleting }] = useDeleteCompanyBookmarkMutation();

  const isLoading = (existingBookmark === undefined && !hasFailed) || isCreating || isDeleting;

  async function handleBookmarkClick() {
    // Whether the company is bookmarked is unknown, so neither adding nor removing is safe. Ask again.
    if (hasFailed) {
      retry();
      return;
    }
    try {
      if (existingBookmark) {
        await deleteCompanyBookmark({
          bookmarkId: existingBookmark.id,
          companyId: existingBookmark.companyId,
        }).unwrap();
        enqueueSnackbar("Removed bookmark", { variant: "info" });
      } else {
        if (!companyId) throw new Error("Bookmark Company: Company ID is undefined");
        await createCompanyBookmark({ companyId: companyId.value }).unwrap();
        enqueueSnackbar("Added bookmark", { variant: "info" });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Couldn't save bookmark", { variant: "error" });
    }
  }

  return (
    <BookmarkToggle
      className={className}
      name={name}
      bookmarked={Boolean(existingBookmark)}
      disabled={!companyId || isLoading}
      onToggle={handleBookmarkClick}
    />
  );
}
